import React, { useLayoutEffect, useRef, useState } from 'react';

import { formatValue } from './format';
import type { MeasureFormat, NumberRange } from './types';

type RangePickProps = {
  label: string;
  range?: NumberRange;
  format?: MeasureFormat;
  onPick?: (range: NumberRange | undefined) => void;
  onRemove?: () => void;
};

const WIDTH = 220;

const bound = (text: string) => (text.trim() === '' || !Number.isFinite(Number(text)) ? undefined : Number(text));

export function rangeSummary(range: NumberRange | undefined, format?: MeasureFormat): string {
  const shown = (value: number) => formatValue(value, format ?? 'num2', { numbers: 'full' });
  if (range?.min !== undefined && range.max !== undefined) return `${shown(range.min)} to ${shown(range.max)}`;
  if (range?.min !== undefined) return `${shown(range.min)} or more`;
  if (range?.max !== undefined) return `Up to ${shown(range.max)}`;
  return 'Any';
}

export default function RangePick({ label, range, format, onPick, onRemove }: RangePickProps) {
  const root = useRef<HTMLSpanElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const first = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [at, setAt] = useState<{ top: number; left: number; width: number }>();
  const set = range?.min !== undefined || range?.max !== undefined;

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) opener.current?.focus();
  };
  const apply = () => {
    const [low, high] = [bound(min), bound(max)];
    const swapped = low !== undefined && high !== undefined && low > high;
    const next = { ...((swapped ? high : low) !== undefined ? { min: swapped ? high : low } : {}), ...((swapped ? low : high) !== undefined ? { max: swapped ? low : high } : {}) };
    onPick?.(Object.keys(next).length ? next : undefined);
    close(true);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    setMin(range?.min === undefined ? '' : String(range.min));
    setMax(range?.max === undefined ? '' : String(range.max));
    const box = root.current?.getBoundingClientRect();
    if (box) setAt({ top: box.bottom + 4, left: Math.max(8, Math.min(box.left, window.innerWidth - WIDTH - 8)), width: WIDTH });
    first.current?.focus();
    const outside = (event: MouseEvent) => !root.current?.contains(event.target as Node) && close(false);
    window.document.addEventListener('mousedown', outside, true);
    return () => window.document.removeEventListener('mousedown', outside, true);
  }, [open]);

  return (
    <span className={`chip${set ? ' set' : ''}`} ref={root}>
      {label}
      <span className="cv">
        <span className="cv-head">{rangeSummary(range, format)}</span>
      </span>
      {onPick ? (
        <button aria-expanded={open} aria-haspopup="dialog" aria-label={label} className="pick-open" onClick={() => (open ? close(false) : setOpen(true))} ref={opener} title={`${label}: ${rangeSummary(range, format)}`} type="button" />
      ) : null}
      {onRemove ? (
        <button aria-label={`Remove the ${label} filter`} className="chip-x" onClick={onRemove} title="Remove this filter" type="button">
          ✕
        </button>
      ) : null}
      {open ? (
        <div
          aria-label={label}
          className="pick-pop range-pop"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close(true);
            } else if (event.key === 'Enter') apply();
          }}
          role="dialog"
          style={at}
          tabIndex={-1}
        >
          <label>
            From
            <input aria-label={`${label} from`} inputMode="decimal" onChange={(event) => setMin(event.target.value)} placeholder="Any" ref={first} value={min} />
          </label>
          <label>
            To
            <input aria-label={`${label} to`} inputMode="decimal" onChange={(event) => setMax(event.target.value)} placeholder="Any" value={max} />
          </label>
          <div className="pick-foot">
            <button onClick={apply} type="button">
              Apply
            </button>
            {set ? (
              <button
                onClick={() => {
                  onPick?.(undefined);
                  close(true);
                }}
                type="button"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </span>
  );
}
